import {
  FILTER_CATEGORIES,
  SEARCH_SORT_VALUES,
  SORT_URL_PARAM,
  VALID_SEARCH_URL_PARAMS,
} from "@/components/Search/constants";
import { mapSearchParamToDcs } from "@/components/Search/utils/mapSearchParamsToDcs";

type SearchParamsRecord = Readonly<
  Record<string, string | readonly string[] | undefined>
>;

interface SearchParamsReader {
  getAll: (key: string) => string[];
}

type SearchParamsInput = SearchParamsRecord | SearchParamsReader;

const isSearchParamsReader = (
  searchParams: SearchParamsInput,
): searchParams is SearchParamsReader =>
  "getAll" in searchParams && typeof searchParams.getAll === "function";

const getParamValues = (
  searchParams: SearchParamsInput,
  key: string,
): readonly string[] => {
  if (isSearchParamsReader(searchParams)) {
    return searchParams.getAll(key);
  }

  const value = searchParams[key];
  if (value === undefined) return [];
  return Array.isArray(value) ? value : [value];
};

const supportedSortValues = new Set<string>(SEARCH_SORT_VALUES);
const facetUrlKeys = new Set<string>(FILTER_CATEGORIES);

const collectMappedValues = (
  searchParams: SearchParamsInput,
  urlKeys: readonly string[],
): Readonly<Record<string, string>> => {
  const mappedValues = new Map<string, string[]>();

  urlKeys.forEach((urlKey) => {
    const values = getParamValues(searchParams, urlKey).filter(
      (value) =>
        value !== "" &&
        (urlKey !== SORT_URL_PARAM || supportedSortValues.has(value)),
    );
    if (values.length === 0) return;

    const mappedKey = mapSearchParamToDcs(urlKey);
    if (!facetUrlKeys.has(urlKey)) {
      if (!mappedValues.has(mappedKey)) {
        mappedValues.set(mappedKey, [values[0]]);
      }
      return;
    }

    mappedValues.set(mappedKey, [
      ...(mappedValues.get(mappedKey) ?? []),
      ...values,
    ]);
  });

  return Object.fromEntries(
    [...mappedValues].map(([key, values]) => [key, values.join(",")]),
  );
};

/**
 * Builds the mount seed from the approved search URL mapping.
 * An unapproved query param is left out.
 */
export const buildInitialFormState = (
  searchParams: SearchParamsInput,
): Readonly<Record<string, string>> =>
  collectMappedValues(searchParams, VALID_SEARCH_URL_PARAMS);

/**
 * The host allowlist for post-mount changes. Derived from the approved URL
 * mapping, not from the keys present on the landing URL, so a facet selected
 * after load is still reported.
 */
export const buildObservedFormStateKeys = (): readonly string[] => [
  ...new Set(VALID_SEARCH_URL_PARAMS.map(mapSearchParamToDcs)),
];
